import { FLAGS } from '@iheartjane/feature-flags';
import { useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { MenuHeader } from '@jane/bloom/components/menuHeader/menuHeader';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { RecommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { DEFAULT_TITLE } from '@jane/bloom/resources/meta/routes/deals';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { sortSpecials } from '@jane/bloom/utils/specials/sortSpecials';

import { DealsTab } from './components/dealsTab';

export function DealsRouteComponent() {
  const { customSEO, featureFlags, metaProps, theme } = useRootLoaderData();
  const useV3StoreSpecials = featureFlags[FLAGS.c3UseV3StoreSpecials];
  const {
    availableFilters,
    brandSpecials,
    complianceLanguage,
    customLanguage,
    enabledSpecialsForBanner,
    enabledSpecialsForRow: enabledStoreSpecials,
    hasLinkedBank,
    janeGold,
    menuProductImgsAboveTheFold,
    recommendedSortResults,
    searchCriteria,
    sort: { currentSort, sortOptions },
  } = useLoaderData<RecommendedSortResults>();

  const storeSpecials = useMemo(() => {
    const specials = Object.values(enabledStoreSpecials).flat();

    if (!isEmpty(specials)) {
      return useV3StoreSpecials ? specials : sortSpecials([...specials]);
    }
    return null;
  }, [enabledStoreSpecials, useV3StoreSpecials]);

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
      theme,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <MenuHeader />
      <SeoHeading
        customSEOPage={customSEO?.deal}
        defaultTitle={DEFAULT_TITLE}
        metaVariables={createMetaVariables(metaProps)}
      />

      <BloomWizardModal
        brandSpecials={brandSpecials}
        specials={enabledStoreSpecials}
      >
        {({ openModal }) => (
          <DealsTab
            brandSpecials={brandSpecials}
            customLanguage={customLanguage}
            currentSort={currentSort}
            enabledSpecialsForBanner={enabledSpecialsForBanner}
            hasLinkedBank={hasLinkedBank}
            openWizard={openModal}
            preloadedProductIds={menuProductImgsAboveTheFold.map(
              ({ id }) => id
            )}
            sortOptions={sortOptions}
            rowInstance={rowInstance}
            storeSpecials={storeSpecials}
            complianceLanguage={complianceLanguage}
            janeGold={janeGold}
          />
        )}
      </BloomWizardModal>
    </BloomFilterProvider>
  );
}
